import { LitElement, html, css, PropertyValues } from "lit";
import { AssemblyScene } from "../scene/AssemblyScene";
import { AssemblyGraph, StationId, StationDef, STATION_ORDER, isStationId } from "../graph/types";
import { loadAssemblyGraph, glbUrl } from "../graph/loader";
import { computeBom, BomRow } from "../bom/computeBom";
import { postEvent, configureEvents } from "../api/events";

const DEFAULT_ASSET_BASE = "https://embed.makistry.ai/v1/";

/**
 * <makistry-assembly-viewer assembly-id api-key station asset-base>
 *
 * The entire host contract: set `station` (attribute or setStation()) and the
 * component renders the correct 3D state + a synced BOM panel. Pure function of
 * station; idempotent; any nav order works.
 */
export class MakistryAssemblyViewer extends LitElement {
  static properties = {
    assemblyId: { type: String, attribute: "assembly-id" },
    apiKey: { type: String, attribute: "api-key" },
    station: { type: String, reflect: true },
    assetBase: { type: String, attribute: "asset-base" },
    apiBase: { type: String, attribute: "api-base" },
    tools: { type: String },
    bom: { type: String },
    _bom: { state: true },
    _status: { state: true },
    _error: { state: true },
    _toolbarOpen: { state: true },
    _autoRotate: { state: true },
    _edges: { state: true },
    _crossAxis: { state: true },
    _crossPos: { state: true },
    _explode: { state: true },
  };

  static styles = css`
    :host {
      display: block;
      position: relative;
      width: 100%;
      height: 100%;
      min-height: 320px;
      font-family: system-ui, -apple-system, sans-serif;
      /* !important so a hostile host rule (star selector, color, important) cannot
         leak an inherited color across the shadow boundary via the host element. */
      color: #1f2937 !important;
    }
    .stage {
      position: absolute;
      inset: 0;
    }
    .bom {
      position: absolute;
      top: 12px;
      left: 12px;
      width: 240px;
      max-height: calc(100% - 24px);
      overflow-y: auto;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(4px);
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      padding: 10px 12px;
      font-size: 12px;
    }
    .bom h3 {
      margin: 0 0 8px;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #6b7280;
    }
    .row {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      padding: 4px 6px;
      border-radius: 6px;
      line-height: 1.3;
      color: #1f2937;
    }
    .row.new {
      background: #dbeafe;
      font-weight: 600;
    }
    .row .qty {
      color: #6b7280;
      flex-shrink: 0;
    }
    .badge {
      position: absolute;
      bottom: 12px;
      right: 12px;
      padding: 4px 10px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      background: #111827;
      color: #fff;
    }
    .badge.torque {
      background: #b45309;
    }
    .badge.final {
      background: #15803d;
    }
    .msg {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      color: #6b7280;
      pointer-events: none;
    }
    .msg.error {
      color: #dc2626;
    }

    /* ── Toolbar (top-right, collapsible) ── */
    .toolbar {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 200px;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(4px);
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      font-size: 12px;
      color: #1f2937;
    }
    .tb-header {
      display: flex;
      align-items: center;
      gap: 6px;
      width: 100%;
      padding: 9px 11px;
      background: none;
      border: 0;
      cursor: pointer;
      font: inherit;
      color: #374151;
      font-weight: 600;
    }
    .tb-header:hover {
      background: #f3f4f6;
    }
    .tb-header .chev {
      margin-left: auto;
      transition: transform 0.15s;
    }
    .tb-header .chev.open {
      transform: rotate(180deg);
    }
    .tb-body {
      padding: 6px 8px 10px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      border-top: 1px solid #f0f0f0;
    }
    .tb-item {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 7px 8px;
      background: none;
      border: 0;
      border-radius: 7px;
      cursor: pointer;
      font: inherit;
      color: #374151;
      text-align: left;
    }
    .tb-item:hover {
      background: #f3f4f6;
    }
    .tb-item.active {
      background: #dbeafe;
      color: #1d4ed8;
    }
    .tb-item svg {
      flex-shrink: 0;
    }
    .tb-sub {
      display: flex;
      gap: 6px;
      padding: 2px 4px 4px 30px;
    }
    .tb-axis {
      flex: 1;
      padding: 4px 0;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      background: #fff;
      cursor: pointer;
      font: inherit;
      font-weight: 600;
      color: #6b7280;
    }
    .tb-axis.on {
      background: #2563eb;
      border-color: #2563eb;
      color: #fff;
    }
    .tb-slider {
      padding: 2px 8px 4px 30px;
    }
    .tb-explode {
      padding: 6px 8px 2px;
    }
    .tb-explode label {
      display: block;
      margin-bottom: 4px;
      color: #6b7280;
      font-weight: 600;
    }
    input[type="range"] {
      width: 100%;
      accent-color: #2563eb;
      cursor: pointer;
    }
  `;

  assemblyId = "v12-demo";
  apiKey?: string;
  station: string = "SA1";
  assetBase = DEFAULT_ASSET_BASE;
  apiBase?: string;
  /**
   * Which Tools-panel controls to show. Default "all" = auto-rotate, reset view, edges,
   * cross-section, explode. "basic" = auto-rotate + reset only. "none" hides the panel.
   * Or pass a space/comma list (e.g. "auto-rotate reset explode").
   */
  tools = "all";
  /**
   * Show the built-in Bill of Materials panel. "show" (default) renders it; "hide"
   * removes it — for hosts that already display a BOM in their own UI. The
   * makistry-station-change event still fires either way, so a host driving its own BOM
   * loses nothing.
   */
  bom = "show";

  private _bomVisible(): boolean {
    return (this.bom || "show").trim().toLowerCase() !== "hide";
  }

  /** Resolve the `tools` attribute to a set of enabled tool ids (empty = hide panel). */
  private _toolSet(): Set<string> {
    const raw = (this.tools || "all").trim().toLowerCase();
    if (raw === "none") return new Set();
    if (raw === "all") return new Set(["auto-rotate", "reset", "edges", "cross-section", "explode"]);
    const set = new Set(raw.split(/[\s,]+/).filter(Boolean));
    // auto-rotate + reset are the committed default surface (unless explicitly "none").
    set.add("auto-rotate");
    set.add("reset");
    return set;
  }

  private _bom: BomRow[] = [];
  private _status: string | null = "Loading…";
  private _error: string | null = null;
  private _toolbarOpen = false;
  private _autoRotate = false;
  private _edges = false;
  private _crossAxis: "x" | "y" | "z" | null = null;
  private _crossPos = 0.5;
  private _explode = 0;

  private _graph: AssemblyGraph | null = null;
  private _scene: AssemblyScene | null = null;
  private _mount: HTMLDivElement | null = null;
  private _ro: ResizeObserver | null = null;
  private _ready = false;

  render() {
    const overlay = this._graph && isStationId(this.station)
      ? this._graph.stations[this.station].statusOverlay
      : undefined;
    return html`
      <div class="stage" id="stage"></div>
      ${this._bom.length && this._bomVisible()
        ? html`<div class="bom">
            <h3>Bill of Materials</h3>
            ${this._bom.map(
              (r) => html`<div class="row ${r.isNew ? "new" : ""}">
                <span>${r.item.name}</span>
                <span class="qty">×${r.item.qty}</span>
              </div>`
            )}
          </div>`
        : null}
      ${this._ready && this._toolSet().size > 0 ? this._renderToolbar() : null}
      ${overlay ? html`<div class="badge ${overlay}">${overlay}</div>` : null}
      ${this._status ? html`<div class="msg">${this._status}</div>` : null}
      ${this._error ? html`<div class="msg error">${this._error}</div>` : null}
    `;
  }

  private _renderToolbar() {
    const tools = this._toolSet();
    return html`
      <div class="toolbar">
        <button class="tb-header" @click=${() => { this._toolbarOpen = !this._toolbarOpen; }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
          <span>Tools</span>
          <svg class="chev ${this._toolbarOpen ? "open" : ""}" width="14" height="14"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
        </button>
        ${this._toolbarOpen
          ? html`<div class="tb-body">
              <button class="tb-item ${this._autoRotate ? "active" : ""}" @click=${this._toggleAutoRotate}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.85.99 6.57 2.57L21 8"/><path d="M21 3v5h-5"/></svg>
                Auto-rotate
              </button>

              <button class="tb-item" @click=${this._resetView}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
                Reset view
              </button>

              ${tools.has("edges")
                ? html`<button class="tb-item ${this._edges ? "active" : ""}" @click=${this._toggleEdges}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2 2 7l10 5 10-5-10-5Z"/><path d="m2 17 10 5 10-5"/><path d="m2 12 10 5 10-5"/></svg>
                    ${this._edges ? "Hide edges" : "Show edges"}
                  </button>`
                : null}

              ${tools.has("cross-section")
                ? html`<button class="tb-item ${this._crossAxis ? "active" : ""}" @click=${this._toggleCross}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 12h18"/></svg>
                      Cross-section
                    </button>
                    ${this._crossAxis
                      ? html`
                          <div class="tb-sub">
                            ${(["x", "y", "z"] as const).map(
                              (a) => html`<button class="tb-axis ${this._crossAxis === a ? "on" : ""}"
                                @click=${() => this._setCrossAxis(a)}>${a.toUpperCase()}</button>`
                            )}
                          </div>
                          <div class="tb-slider">
                            <input type="range" min="0" max="1" step="0.01" .value=${String(this._crossPos)}
                              @input=${(e: Event) => this._setCrossPos(+(e.target as HTMLInputElement).value)} />
                          </div>`
                      : null}`
                : null}

              ${tools.has("explode")
                ? html`<div class="tb-explode">
                    <label>Explode ${Math.round(this._explode * 100)}%</label>
                    <input type="range" min="0" max="1" step="0.01" .value=${String(this._explode)}
                      @input=${(e: Event) => this._setExplode(+(e.target as HTMLInputElement).value)} />
                  </div>`
                : null}
            </div>`
          : null}
      </div>
    `;
  }

  private _toggleAutoRotate = () => {
    this._autoRotate = !this._autoRotate;
    this._scene?.setAutoRotate(this._autoRotate);
  };
  private _resetView = () => {
    this._autoRotate = false;
    this._scene?.resetView();
  };
  private _toggleEdges = () => {
    this._edges = !this._edges;
    this._scene?.setEdges(this._edges);
  };
  private _toggleCross = () => {
    this._crossAxis = this._crossAxis ? null : "x";
    this._scene?.setCrossSection(this._crossAxis, this._crossPos);
  };
  private _setCrossAxis = (a: "x" | "y" | "z") => {
    this._crossAxis = a;
    this._scene?.setCrossSection(a, this._crossPos);
  };
  private _setCrossPos = (t: number) => {
    this._crossPos = t;
    if (this._crossAxis) this._scene?.setCrossSection(this._crossAxis, t);
  };
  private _setExplode = (v: number) => {
    this._explode = v;
    this._scene?.setExplode(v);
  };

  firstUpdated(): void {
    this._mount = this.renderRoot.querySelector("#stage") as HTMLDivElement;
    this._ro = new ResizeObserver(() => this._scene?.resize());
    this._ro.observe(this._mount);
    void this._boot();
  }

  private async _boot(): Promise<void> {
    if (!this._mount) return;
    // Metering: only on when the host points at an API (api-base). Static/CDN = off.
    configureEvents(this.apiBase ? `${this.apiBase}events` : null);
    try {
      const graph = await loadAssemblyGraph({
        assemblyId: this.assemblyId,
        apiKey: this.apiKey,
        assetBase: this.assetBase,
        apiBase: this.apiBase,
      });
      this._graph = graph;
      const initial = isStationId(this.station) ? this.station : "SA1";
      this._scene = new AssemblyScene(this._mount);
      // Draco decoder lives alongside the assets so compressed GLBs decode.
      const dracoPath = `${this.assetBase}draco/`;
      await this._scene.load(glbUrl(this.assetBase, this.assemblyId), graph, initial, dracoPath);
      this._ready = true;
      this._status = null;
      this._applyStation(initial);
      this._emit("makistry-ready", { assemblyId: this.assemblyId, stations: this.stations });
    } catch (e) {
      const message = `Failed to load assembly: ${(e as Error).message ?? e}`;
      this._error = message;
      this._status = null;
      this._emit("makistry-error", { message });
    }
  }

  /** Ordered list of station ids present in the loaded graph (empty until ready). */
  get stations(): StationId[] {
    if (!this._graph) return [];
    return STATION_ORDER.filter((s) => this._graph!.stations[s]);
  }

  /** Dispatch a bubbling, shadow-crossing CustomEvent so any host can react. */
  private _emit(name: string, detail: unknown): void {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  updated(changed: PropertyValues): void {
    if (changed.has("station") && this._ready) {
      this._applyStation(this.station);
    }
  }

  /** Public API: identical path to setAttribute('station', id). */
  setStation(id: string): void {
    this.station = id;
  }

  private _applyStation(raw: string): void {
    if (!this._graph || !this._scene) return;
    if (!isStationId(raw)) {
      console.warn(`[makistry-assembly-viewer] unknown station "${raw}"`);
      return;
    }
    const id = raw as StationId;
    const def: StationDef = this._graph.stations[id];
    this._scene.renderState(id);
    this._bom = computeBom(this._graph, id);
    postEvent({ apiKey: this.apiKey, assemblyId: this.assemblyId, station: id, ts: performance.now() });
    this._emit("makistry-station-change", { station: id, label: def.label });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._ro?.disconnect();
    this._scene?.dispose();
    this._scene = null;
  }
}
